import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, ShieldCheck, CheckCircle2, FileText, Lock, Clock } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { nb, nok } from "@/lib/format";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function ExternalSign() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [signed, setSigned] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/sign/${token}`);
      setInfo(data);
      if (data.signed) setSigned(true);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // Fang opp retur fra BankID: synk ordrestatus når siden lastes.
  useEffect(() => {
    api.post(`/sign/${token}/sync`).then(({ data }) => {
      if (data.signed) { setSigned(true); load(); }
    }).catch(() => {});
  }, [token, load]);

  const startBankid = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/sign/${token}/bankid`);
      window.location.assign(data.signing_url);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setBusy(false);
    }
  };

  if (error) {
    return (
      <div className="grid min-h-screen place-items-center bg-background px-4">
        <div className="max-w-md rounded-2xl border border-border bg-card p-8 text-center" data-testid="external-sign-error">
          <Lock className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
          <h1 className="font-headings text-xl font-bold">Lenken kan ikke åpnes</h1>
          <p className="mt-2 text-sm text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  if (!info) {
    return (
      <div className="grid min-h-screen place-items-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30 py-8 px-4">
      <div className="mx-auto max-w-xl" data-testid="external-sign-page">
        <div className="overflow-hidden rounded-t-2xl bg-[#0f1a13] px-6 py-6 text-[#eaf87a]">
          <div className="text-2xl font-extrabold tracking-tight">murphy.</div>
          <div className="mt-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#cfe86a]">Signering med BankID</div>
        </div>

        <div className="rounded-b-2xl border border-t-0 border-border bg-card p-6 sm:p-8">
          <h1 className="font-headings text-2xl font-extrabold tracking-tight" data-testid="external-sign-title">{info.entity_title}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{info.company_name}</p>

          <div className="mt-5 flex items-center gap-3 rounded-xl border border-primary/25 bg-primary/[0.04] px-4 py-3">
            <ShieldCheck className="h-5 w-5 shrink-0 text-primary" />
            <div className="text-sm">
              <span className="font-semibold">{info.party_name}</span>
              <span className="text-muted-foreground"> · {info.party_role}</span>
            </div>
          </div>

          {info.shares ? (
            <div className="mt-4 rounded-xl border border-border bg-background px-4 py-3 text-sm">
              <p><span className="text-muted-foreground">Handel:</span> {nb(info.shares)} {info.share_class}{info.price_per_share ? ` · ${nok(info.price_per_share)}/aksje` : ""}</p>
            </div>
          ) : null}

          <a href={`${BACKEND}/api/sign/${token}/pdf`} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline" data-testid="external-sign-pdf">
            <FileText className="h-4 w-4" /> Les hele dokumentet (PDF)
          </a>

          <div className="mt-6">
            {signed || info.status === "signed" ? (
              <div className="flex items-center gap-2 rounded-xl border border-emerald-500/30 bg-emerald-500/[0.08] px-4 py-3 text-sm text-emerald-700 dark:text-emerald-300" data-testid="external-sign-done">
                <CheckCircle2 className="h-5 w-5" /> Takk! Signaturen din er registrert.
              </div>
            ) : !info.entity_open ? (
              <div className="flex items-center gap-2 rounded-xl border border-border px-4 py-3 text-sm text-muted-foreground" data-testid="external-sign-closed">
                <Clock className="h-5 w-5" /> Dokumentet er ikke lenger åpent for signering.
              </div>
            ) : !info.configured ? (
              <p className="text-sm text-muted-foreground">BankID-signering er ikke tilgjengelig akkurat nå. Kontakt selskapet.</p>
            ) : (
              <Button size="lg" className="w-full gap-2" disabled={busy} onClick={startBankid} data-testid="external-sign-bankid-btn">
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <ShieldCheck className="h-5 w-5" />} Signer med BankID
              </Button>
            )}
          </div>

          <p className="mt-6 border-t border-border/60 pt-4 text-[11px] text-muted-foreground">
            Denne lenken er personlig og knyttet til deg som part. Del den ikke med andre. Sendt via Murphy Styrerom.
          </p>
        </div>
      </div>
    </div>
  );
}
